import Flickity from 'flickity';
import './hero-met-slider.scss';

document.addEventListener('DOMContentLoaded', () => {
	const sliders = document.querySelectorAll('.o-heroMetSlider__flickity[data-autoplay="true"]');

	sliders.forEach((slider) => {
		bindAutoplayProgress(slider);
	});
});

function getFlickityInstance(el) {
	if (!el) {
		return null;
	}

	const moduleInstance = Flickity?.data?.(el);
	if (moduleInstance) {
		return moduleInstance;
	}

	return window.Flickity?.data?.(el) || null;
}

function waitForFlickity(el, timeout = 8000) {
	return new Promise((resolve) => {
		const existing = getFlickityInstance(el);
		if (existing) {
			resolve(existing);
			return;
		}

		const observer = new MutationObserver(() => {
			const instance = getFlickityInstance(el);
			if (instance) {
				observer.disconnect();
				resolve(instance);
			}
		});

		observer.observe(el, { attributes: true, attributeFilter: ['class'] });

		setTimeout(() => {
			observer.disconnect();
			resolve(getFlickityInstance(el));
		}, timeout);
	});
}

function getDurationMs(slider) {
	const fromData = parseFloat(slider.dataset.duration);
	if (!Number.isNaN(fromData) && fromData > 0) {
		return fromData * 1000;
	}

	const fromCss = parseFloat(slider.style.getPropertyValue('--hero-track-duration'));
	if (!Number.isNaN(fromCss) && fromCss > 0) {
		return fromCss * 1000;
	}

	return 5000;
}

function bindAutoplayProgress(slider) {
	const durationMs = getDurationMs(slider);
	const pauseOnHover = slider.dataset.pauseOnHover === 'true';
	let flkty = null;
	let elapsedMs = 0;
	let lastTick = Date.now();
	let rafId = null;
	let isRunning = false;
	let isHoverPaused = false;
	let isTransitioning = false;

	const setProgress = (progress) => {
		slider.style.setProperty('--hero-progress', String(Math.min(1, Math.max(0, progress))));
	};

	const stopClock = () => {
		isRunning = false;
		if (rafId) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
	};

	const tick = () => {
		if (!isRunning) {
			return;
		}

		const now = Date.now();
		elapsedMs += now - lastTick;
		lastTick = now;

		const progress = elapsedMs / durationMs;
		setProgress(progress);

		if (progress >= 1) {
			stopClock();
			if (flkty) {
				flkty.next(true);
			}
			return;
		}

		rafId = requestAnimationFrame(tick);
	};

	const playClock = () => {
		if (isHoverPaused || isTransitioning) {
			return;
		}

		stopClock();
		isRunning = true;
		lastTick = Date.now();
		rafId = requestAnimationFrame(tick);
	};

	// Hold the progress bar at zero while the slide is still animating into place.
	const onSelect = () => {
		isTransitioning = true;
		elapsedMs = 0;
		setProgress(0);
		stopClock();
	};

	const onSettle = () => {
		isTransitioning = false;
		playClock();
	};

	const pause = () => {
		if (!pauseOnHover || isHoverPaused) {
			return;
		}

		isHoverPaused = true;
		slider.classList.add('pauze');

		if (isRunning) {
			const now = Date.now();
			elapsedMs += now - lastTick;
			lastTick = now;
			setProgress(elapsedMs / durationMs);
		}

		stopClock();
	};

	const resume = () => {
		if (!pauseOnHover || !isHoverPaused) {
			return;
		}

		isHoverPaused = false;
		slider.classList.remove('pauze');
		playClock();
	};

	if (pauseOnHover) {
		slider.addEventListener('mouseenter', pause);
		slider.addEventListener('mouseleave', resume);
		slider.addEventListener('pointerenter', pause);
		slider.addEventListener('pointerleave', resume);
	}

	document.addEventListener('visibilitychange', () => {
		lastTick = Date.now();
	});

	waitForFlickity(slider).then((instance) => {
		if (!instance) {
			return;
		}

		flkty = instance;
		flkty.stopPlayer();
		flkty.options.autoPlay = false;
		flkty.options.pauseAutoPlayOnHover = false;

		flkty.on('select', onSelect);
		flkty.on('settle', onSettle);

		isHoverPaused = pauseOnHover && slider.matches(':hover');
		slider.classList.toggle('pauze', isHoverPaused);
		onSettle();
	});
}
